import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { InitialsAvatar } from "@/components/InitialsAvatar";
import { StageBadge, TypeBadge, TemperatureBadge } from "@/components/Badges";
import { EmptyState } from "@/components/EmptyState";
import { ListSkeleton } from "@/components/Skeletons";
import { ContactDialog } from "@/components/ContactDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { relativeDate } from "@/lib/format";
import { Plus, Search, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Contactos() {
    const [items, setItems] = useState(null);
    const [meta, setMeta] = useState({ total: 0, page: 1, pages: 1 });
    const [type, setType] = useState("all");
    const [q, setQ] = useState("");
    const [page, setPage] = useState(1);
    const [dialogOpen, setDialogOpen] = useState(false);

    const load = useCallback(async () => {
        setItems(null);
        try {
            const params = { page, limit: 12 };
            if (type !== "all") params.type = type;
            if (q.trim()) params.q = q.trim();
            const { data } = await api.get("/contacts", { params });
            setItems(data.items);
            setMeta({ total: data.total, page: data.page, pages: data.pages });
        } catch (e) {
            toast.error(errMsg(e));
            setItems([]);
        }
    }, [type, q, page]);

    useEffect(() => {
        const t = setTimeout(load, q ? 300 : 0);
        return () => clearTimeout(t);
    }, [load, q]);

    const onSaved = () => load();

    return (
        <div className="space-y-8" data-testid="contactos-page">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight text-nm-ink">Contactos</h1>
                    <p className="mt-1 text-nm-muted">
                        <span className="tabular-nums">{meta.total}</span> personas en tu cartera
                    </p>
                </div>
                <Button onClick={() => setDialogOpen(true)} data-testid="add-contact-button">
                    <Plus className="mr-1 h-4 w-4" /> Nuevo contacto
                </Button>
            </div>

            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <Tabs value={type} onValueChange={(v) => { setType(v); setPage(1); }}>
                    <TabsList data-testid="contact-type-tabs">
                        <TabsTrigger value="all" data-testid="tab-all">Todos</TabsTrigger>
                        <TabsTrigger value="cliente" data-testid="tab-cliente">Clientes</TabsTrigger>
                        <TabsTrigger value="equipo" data-testid="tab-equipo">Equipo</TabsTrigger>
                        <TabsTrigger value="sin_clasificar" data-testid="tab-sin">Sin clasificar</TabsTrigger>
                    </TabsList>
                </Tabs>
                <div className="relative w-full sm:w-64">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-nm-muted" />
                    <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
                        placeholder="Buscar por nombre" className="pl-9" data-testid="contact-search" />
                </div>
            </div>

            {items === null ? (
                <ListSkeleton rows={5} />
            ) : items.length === 0 ? (
                <EmptyState testId="contactos-empty" title="Aún no hay contactos aquí"
                    description="Añade a las primeras personas de tu lista. Nosotros las organizamos y te decimos a quién escribir."
                    action={<Button onClick={() => setDialogOpen(true)}><Plus className="mr-1 h-4 w-4" /> Añadir mi primer contacto</Button>} />
            ) : (
                <>
                    <div className="divide-y divide-nm-line overflow-hidden rounded-2xl border border-nm-line bg-nm-surface">
                        {items.map((c) => (
                            <Link key={c.id} to={`/app/contactos/${c.id}`} data-testid={`contact-card-item-${c.id}`}
                                className="flex items-center gap-4 p-4 transition-colors duration-150 hover:bg-nm-accent-soft/40">
                                <InitialsAvatar name={c.name} />
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <p className="truncate font-semibold text-nm-ink">{c.name}</p>
                                        {c.needs_followup && <span className="h-2 w-2 shrink-0 rounded-full bg-nm-accent" title="Necesita seguimiento" />}
                                    </div>
                                    <p className="truncate text-sm text-nm-muted">{c.next_action || c.source || "Sin acción definida"}</p>
                                </div>
                                <div className="hidden items-center gap-2 sm:flex">
                                    <TemperatureBadge temperature={c.temperature} />
                                    <TypeBadge type={c.type} />
                                    <StageBadge stage={c.stage} />
                                </div>
                                <span className="hidden w-20 shrink-0 text-right text-xs text-nm-muted md:block">
                                    {c.last_contacted_at ? relativeDate(c.last_contacted_at) : "Nuevo"}
                                </span>
                                <ArrowRight className="h-4 w-4 shrink-0 text-nm-muted" />
                            </Link>
                        ))}
                    </div>

                    {meta.pages > 1 && (
                        <div className="flex items-center justify-between">
                            <Button variant="outline" size="sm" disabled={page <= 1}
                                onClick={() => setPage((p) => p - 1)} data-testid="page-prev">Anterior</Button>
                            <span className="text-sm text-nm-muted tabular-nums">Página {meta.page} de {meta.pages}</span>
                            <Button variant="outline" size="sm" disabled={page >= meta.pages}
                                onClick={() => setPage((p) => p + 1)} data-testid="page-next">Siguiente</Button>
                        </div>
                    )}
                </>
            )}

            <ContactDialog open={dialogOpen} onOpenChange={setDialogOpen} onSaved={onSaved} />
        </div>
    );
}
